import { type Key, useEffect } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { Controller, useWatch } from 'react-hook-form';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { successCriteriaDisplayOptions } from '@gonfalon/metrics/constants';
import { isLDHosted } from '@gonfalon/metrics/isLDHosted';
import { RatioMetricDefinitionBox } from '@gonfalon/metrics/RatioMetricDefinitionBox';
import {
  Button,
  FieldGroup,
  Label,
  ListBox,
  ListBoxItem,
  Popover,
  Select,
  SelectValue,
} from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';

import { createAnalysisUnitsSelectHandler } from '../internal/CreateMetricModal/components/analysisUnitsSelection';
import { AnalysisUnitsError, MetricAnalysisUnits } from '../internal/CreateMetricModal/components/MetricAnalysisUnits';
import { RatioTermSection } from '../internal/CreateMetricModal/RatioTermSection';

import { RequiredLabel } from './components/RequiredLabel';
import type { MetricForm, RatioTermAggregation } from './formUtils';
import { defaultDenominatorForm } from './formUtils';

import defStyles from '../internal/CreateMetricModal/MetricDefinitionFormField/MetricDefinitionFormField.module.css';
import styles from './RatioMetricSection.module.css';

interface RatioMetricSectionProps {
  context: UseFormReturn<MetricForm>;
  /** Gives the term cards the sectioned add-on treatment. Only the create modal opts in so far. */
  useSectionedLayout?: boolean;
  canChangeDataSource?: boolean;
}

export const RatioMetricSection: React.FC<RatioMetricSectionProps> = ({
  context,
  useSectionedLayout = false,
  canChangeDataSource = true,
}) => {
  const {
    control,
    setValue,
    formState: { errors },
  } = context;

  const denominator = useWatch({ control, name: 'denominator' });
  const successCriteria = useWatch({ control, name: 'successCriteria', exact: true });
  const analysisUnits = useWatch({ control, name: 'analysisUnits', exact: true }) ?? [];
  const dataSourceKey = useWatch({ control, name: 'dataSourceKey' });

  // Ensure denominator defaults are set when ratio mode becomes active.
  const denominatorDataSourceKey = denominator?.dataSourceKey;
  useEffect(() => {
    const warehouseKey = isLDHosted(dataSourceKey) ? undefined : dataSourceKey;
    if (!denominator) {
      setValue('denominator', { ...defaultDenominatorForm, dataSourceKey: warehouseKey }, { shouldDirty: false });
      return;
    }
    if (denominatorDataSourceKey === undefined && warehouseKey !== undefined) {
      setValue('denominator.dataSourceKey', warehouseKey, { shouldDirty: false });
    }
  }, [denominator, denominatorDataSourceKey, dataSourceKey, setValue]);

  const numeratorUnitAggType = useWatch({ control, name: 'unitAggregationType' });
  const numeratorIsNumeric = useWatch({ control, name: 'isNumeric' });
  const numeratorUnitAggField = useWatch({ control, name: 'unitAggregationField' });

  function getNumeratorAggParts(): { base: string; column?: string } {
    if (numeratorUnitAggType === 'count_distinct') {
      return {
        base: msg('metrics.metricDefinition.aggregationCountDistinct'),
        column: numeratorUnitAggField || msg('metrics.metricDefinition.columnNamePlaceholder'),
      };
    }
    if (numeratorIsNumeric && numeratorUnitAggType === 'sum') {
      return { base: msg('metrics.metricDefinition.aggregationSum') };
    }
    if (numeratorIsNumeric && numeratorUnitAggType === 'average') {
      return { base: msg('metrics.metricDefinition.aggregationAverage') };
    }
    return { base: msg('metrics.metricDefinition.aggregationCount') };
  }

  function getDenominatorAggParts(): { base: string; column?: string } {
    const agg: RatioTermAggregation = denominator?.aggregationType ?? 'count';
    const field = denominator?.unitAggregationField;
    if (agg === 'count_distinct') {
      return {
        base: msg('metrics.metricDefinition.aggregationCountDistinct'),
        column: field || msg('metrics.metricDefinition.columnNamePlaceholder'),
      };
    }
    if (agg === 'sum') {
      return { base: msg('metrics.metricDefinition.aggregationSum') };
    }
    if (agg === 'average') {
      return { base: msg('metrics.metricDefinition.aggregationAverage') };
    }
    return { base: msg('metrics.metricDefinition.aggregationCount') };
  }

  const analysisUnitsOnSelect = createAnalysisUnitsSelectHandler(context);

  const analysisUnitsForm = (
    <MetricAnalysisUnits
      initialValues={analysisUnits.map((au: { value: string }) => au.value)}
      onSelect={analysisUnitsOnSelect}
      dataSourceKey={dataSourceKey}
      shouldFillWidth={useSectionedLayout}
    />
  );

  function handleSuccessCriteriaChange(key: Key | null) {
    if (key === 'HigherThanBaseline' || key === 'LowerThanBaseline') {
      setValue('successCriteria', key, { shouldDirty: true });
    }
  }

  return (
    <div className={styles.ratioSection}>
      {/* Two-column numerator / denominator layout */}
      <div className={styles.termColumns}>
        <RatioTermSection
          context={context}
          label={msg('metrics.metricDefinition.numeratorLabel')}
          isNumerator
          useSectionedLayout={useSectionedLayout}
          canChangeDataSource={canChangeDataSource}
        />
        <RatioTermSection
          context={context}
          label={msg('metrics.metricDefinition.denominatorLabel')}
          isNumerator={false}
          useSectionedLayout={useSectionedLayout}
          canChangeDataSource={canChangeDataSource}
        />
      </div>

      {/* Metric definition + analysis unit + success criteria, bordered top and bottom */}
      <div className={styles.definitionSection}>
        <FieldGroup>
          <RequiredLabel label={msg('metrics.metricDefinition.definitionLabel')} htmlFor="ratioMetricDefinition" />
          <RatioMetricDefinitionBox
            id="ratioMetricDefinition"
            numeratorAggLabel={getNumeratorAggParts().base}
            numeratorAggColumn={getNumeratorAggParts().column}
            denominatorAggLabel={getDenominatorAggParts().base}
            denominatorAggColumn={getDenominatorAggParts().column}
            units={analysisUnits.map((au: { value: string }) => au.value)}
          />
        </FieldGroup>

        <div className={styles.formRow}>
          <Label className={styles.formRowLabel}>
            <Message id="metrics.metricDefinition.analysisUnitLabel" />
          </Label>
          {analysisUnitsForm}
        </div>
        <AnalysisUnitsError
          message={errors.analysisUnits?.message}
          dataSourceKey={dataSourceKey}
          useSectionedLayout={useSectionedLayout}
        />

        <div className={styles.formRow}>
          <Label className={styles.formRowLabel}>
            <Message id="metrics.metricDefinition.successCriteriaLabel" />
          </Label>
          <Controller
            control={control}
            name="successCriteria"
            render={() => (
              <Select
                onChange={handleSuccessCriteriaChange}
                value={successCriteria ?? 'HigherThanBaseline'}
                aria-label={msg('metrics.metricDefinition.successCriteriaPickerLabel')}
              >
                <Button data-test-id="success-criteria-button">
                  <SelectValue />
                  <Icon name="chevron-down" size="small" />
                </Button>
                <Popover className={defStyles.popover}>
                  <ListBox>
                    {successCriteriaDisplayOptions().map((option) => (
                      <ListBoxItem key={option.value} id={option.value}>
                        {option.displayName}
                      </ListBoxItem>
                    ))}
                  </ListBox>
                </Popover>
              </Select>
            )}
          />
        </div>
      </div>
    </div>
  );
};
